import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import T from '../styles/theme';
import { Avatar, Badge, Btn, ProgressBar, Select, Checkbox } from '../components/UI';
import Icon from '../components/Icon';
import { useStore } from '../data/store';
import { useAuth } from '../data/auth';
import { useToast } from '../components/Toast';
import { initDevice, makeCall, hangUp, toggleMute, onCallEvent, offCallEvent, transferCall, acceptIncoming, rejectIncoming, getStatus } from '../data/twilio';
import { logCall as logCallToDb, retryFailedLogs } from '../data/callLogger';
import apiFetch from '../data/api';
import supabase from '../data/supabase';

/* ── Recording Play Button (proxied through server) ── */
function PlayBtn({ url }) {
  const [playing, setPlaying] = React.useState(false);
  const audioRef = React.useRef(null);
  const recSid = (url || "").match(/Recordings\/(RE[a-f0-9]+)/i)?.[1];
  const proxyUrl = recSid ? `/api/twilio/play?sid=${recSid}` : url;
  React.useEffect(() => {
    if (!proxyUrl) return;
    const a = new Audio(proxyUrl);
    audioRef.current = a;
    a.addEventListener("ended", () => setPlaying(false));
    return () => { a.pause(); a.src = ""; };
  }, [proxyUrl]);
  const toggle = (e) => {
    e.stopPropagation();
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(false); setPlaying(true); }
  };
  if (!proxyUrl) return null;
  return (
    <button onClick={toggle} style={{ width: 28, height: 28, borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: playing ? "rgba(64,89,173,0.15)" : "transparent", color: "#FAFAFA", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {playing ? <svg width="10" height="10" viewBox="0 0 10 10" fill="#FAFAFA"><rect x="1" y="1" width="3" height="8"/><rect x="6" y="1" width="3" height="8"/></svg>
        : <svg width="10" height="12" viewBox="0 0 10 12" fill="#FAFAFA"><path d="M0 0L10 6L0 12Z"/></svg>}
    </button>
  );
}

/* ── Campaign Engine Helpers ── */
function canDial(entry, campaign) {
  if (entry.status === "reached" || entry.status === "removed" || entry.status === "exhausted") return false;
  if (entry.attempts >= campaign.maxAttempts) return false;
  if (entry.lastAttempt) {
    const cooldownMs = (campaign.cooldownHours || 24) * 3600000;
    if (Date.now() - new Date(entry.lastAttempt).getTime() < cooldownMs) return false;
  }
  const hour = new Date().getHours();
  if (hour < (campaign.callWindowStart || 9) || hour >= (campaign.callWindowEnd || 17)) return false;
  return true;
}

function getNextStep(entry, campaign) {
  const steps = campaign.steps || [];
  return steps[entry.currentStep || 0] || null;
}

// ── Styles ──
const crd = { background: "#2C2E33", border: "1px solid rgba(217,219,221,0.08)", borderRadius: 8 };
const inp = { padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.05)", color: "#FAFAFA", fontSize: 12, outline: "none", fontFamily: "inherit" };
const lbl = { fontSize: 9, color: T.tx3, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 4 };
const tabBtn = (active) => ({ padding: "6px 14px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 10, fontFamily: "inherit", background: active ? "rgba(64,89,173,0.15)" : "transparent", color: active ? "#FAFAFA" : T.tx3 });

export default function DialerPage({ isMobile }) {
  const store = useStore();
  const auth = useAuth();
  const toast = useToast();
  const { contacts, callQueue, logCall, setCallQueue, deals } = store;

  // ── Core State ──
  const [calling, setCalling] = useState(false);
  const [time, setTime] = useState(0);
  const [qi, setQi] = useState(0);
  const [disp, setDisp] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [dialerReady, setDialerReady] = useState(false);
  const [dialerStatus, setDialerStatus] = useState("Not initialized");
  const [dialerHealth, setDialerHealth] = useState("unknown");
  const [muted, setMuted] = useState(false);
  // True while a dial is in flight — between handleStartCall firing and
  // Twilio's connected/ended/error event. Used to disable the Call button
  // and show a "DIALING..." indicator so the rep doesn't impatiently
  // double-click during the otherwise-invisible 200-2000ms transition
  // window. SDK-level _makeCallInFlight guard in twilio.js is the
  // backstop; this is the UX-level prevention.
  const [dialing, setDialing] = useState(false);
  const [manualNumber, setManualNumber] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);

  // ── Power Dialer State ──
  const [powerMode, setPowerMode] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const [autoDialDelay, setAutoDialDelay] = useState(5); // seconds between calls
  const [autoDialCountdown, setAutoDialCountdown] = useState(0);
  const [showSessionConfig, setShowSessionConfig] = useState(false);
  const [sessionStats, setSessionStats] = useState({ calls: 0, connected: 0, totalTime: 0 });
  const autoDialTimerRef = useRef(null);
  // Refs synced to current callQueue / qi so autoDialNext can read the
  // POST-removal state when its setInterval fires. Without these, the
  // interval callback closes over the pre-removal snapshot and re-dials
  // the just-dispositioned contact (or no-ops on a stale/clamped index).
  const callQueueRef = useRef([]);
  const qiRef = useRef(0);

  // ── Caller ID State ──
  const [callerIdPreview, setCallerIdPreview] = useState(null);
  const [orgNumbers, setOrgNumbers] = useState([]);
  const [selectedCallerId, setSelectedCallerId] = useState(null);
  const [showCallerIdPicker, setShowCallerIdPicker] = useState(false);
  const [localPresence, setLocalPresence] = useState(true);
  const [callRecording, setCallRecording] = useState(true);

  // ── Tab State ──
  const [activeTab, setActiveTab] = useState("intel"); // intel | notes
  const [noteSaved, setNoteSaved] = useState(false);
  const [expandedDocs, setExpandedDocs] = useState(new Set());

  // ── Quick SMS & Email ──
  const [smsText, setSmsText] = useState("");
  const [smsSending, setSmsSending] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [showEmailCompose, setShowEmailCompose] = useState(false);
  const [showSmsCompose, setShowSmsCompose] = useState(false);

  const sendQuickSms = async () => {
    if (!smsText.trim() || !activeContact?.ph) return;
    setSmsSending(true);
    try {
      await apiFetch("/api/sms/send", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ to: activeContact.ph || activeContact.phone, body: smsText, orgId: auth.currentUser?._orgId }) });
      setSmsSent(true); setSmsText(""); setTimeout(() => setSmsSent(false), 2000);
    } catch (e) { console.warn("[CoreFlow] SMS send failed:", e.message); }
    finally { setSmsSending(false); }
  };

  const sendQuickEmail = async () => {
    if (!emailBody.trim() || !activeContact?.email) return;
    setEmailSending(true);
    try {
      await apiFetch("/api/email/send", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ to: activeContact.email, subject: emailSubject || "Following up", body: emailBody, orgId: auth.currentUser?._orgId, userId: auth.currentUser?._dbId }) });
      setEmailSent(true); setEmailSubject(""); setEmailBody(""); setTimeout(() => { setEmailSent(false); setShowEmailCompose(false); }, 2000);
    } catch (e) { console.warn("[CoreFlow] Email send failed:", e.message); }
    finally { setEmailSending(false); }
  };

  // ── Resources (team-level overrides org-level) ──
  const resourcesKey = "cf_dialer_resources_" + (auth.currentUser?._orgId || "default");
  const [resources, setResources] = useState(() => {
    try { return JSON.parse(localStorage.getItem(resourcesKey)) || { bookingLinks: [], scripts: [], docs: [] }; } catch { return { bookingLinks: [], scripts: [], docs: [] }; }
  });
  const [userTeam, setUserTeam] = useState(null);
  const saveResources = useCallback((res) => {
    const data = res || resources;
    const clean = {
      bookingLinks: (data.bookingLinks || []).filter(b => b.name && b.url).map(({ id, name, url }) => ({ id, name, url })),
      scripts: (data.scripts || []).filter(s => s.name || s.content).map(({ id, name, content }) => ({ id, name, content, editing: false })),
      docs: (data.docs || []).filter(d => d.name && d.url).map(({ id, name, url }) => ({ id, name, url, expanded: false })),
    };
    setResources(clean);
    localStorage.setItem(resourcesKey, JSON.stringify(clean));
    // Save to team if user has one, otherwise org
    const orgId = auth.currentUser?._orgId;
    if (userTeam) {
      supabase.from("teams").update({ dialer_resources: clean }).eq("id", userTeam.id).then(({ error }) => {
        if (error) {
          console.error("[DialerPage] Failed to save team dialer resources:", error);
          toast?.error?.("Failed to save dialer resources: " + error.message);
        } else {
          console.log("[CoreFlow] Team dialer resources saved");
        }
      });
    } else if (orgId) {
      supabase.from("organizations").update({ dialer_resources: clean }).eq("id", orgId).then(({ error }) => {
        if (error) {
          console.error("[DialerPage] Failed to save org dialer resources:", error);
          toast?.error?.("Failed to save dialer resources: " + error.message);
        } else {
          console.log("[CoreFlow] Org dialer resources saved");
        }
      });
    }
  }, [resources, auth.currentUser?._orgId, userTeam, toast]);
  // Load resources: check team first, then org
  useEffect(() => {
    const orgId = auth.currentUser?._orgId;
    const userId = auth.currentUser?._dbId;
    if (!orgId) return;
    (async () => {
      // Find user's team
      let teamRes = null;
      if (userId) {
        const { data: membership } = await supabase.from("team_members").select("team_id").eq("user_id", userId).limit(1).maybeSingle();
        if (membership?.team_id) {
          const { data: team } = await supabase.from("teams").select("*").eq("id", membership.team_id).single();
          if (team) {
            setUserTeam(team);
            if (team.dialer_resources && (team.dialer_resources.bookingLinks?.length || team.dialer_resources.scripts?.length || team.dialer_resources.docs?.length)) {
              teamRes = team.dialer_resources;
            }
          }
        }
      }
      // Use team resources if they exist, otherwise org resources
      if (teamRes) {
        const clean = { bookingLinks: (teamRes.bookingLinks || []).map(b => ({ ...b })), scripts: (teamRes.scripts || []).map(s => ({ ...s, editing: false })), docs: (teamRes.docs || []).map(d => ({ ...d, expanded: false })) };
        setResources(clean);
        localStorage.setItem(resourcesKey, JSON.stringify(clean));
        console.log("[CoreFlow] Loaded team dialer resources");
      } else {
        const { data } = await supabase.from("organizations").select("dialer_resources").eq("id", orgId).single();
        if (data?.dialer_resources) {
          const r = data.dialer_resources;
          const clean = { bookingLinks: (r.bookingLinks || []).map(b => ({ ...b })), scripts: (r.scripts || []).map(s => ({ ...s, editing: false })), docs: (r.docs || []).map(d => ({ ...d, expanded: false })) };
          setResources(clean);
          localStorage.setItem(resourcesKey, JSON.stringify(clean));
          console.log("[CoreFlow] Loaded org dialer resources");
        }
      }
    })();
  }, [auth.currentUser?._orgId, auth.currentUser?._dbId]);

  // ── AI Intel State ──
  const [preCallIntel, setPreCallIntel] = useState(null);
  const [preCallLoading, setPreCallLoading] = useState(false);
  const preCallContactRef = useRef(null);
  const [postCallSummary, setPostCallSummary] = useState(null);
  const [postCallLoading, setPostCallLoading] = useState(false);

  // ── Call History for current contact ──
  const [callHistory, setCallHistory] = useState([]);

  // ── Dispositions (live from store, populated by migration 071 + AdminCenter) ──
  // The pre-DB hardcoded list and localStorage-cached custom_dispositions
  // JSONB column have been retired. Source of truth: call_dispositions table
  // (Commit A migration 071) loaded via store boot orchestration (Commit B).
  // Disabled rows are filtered out so the picker only shows active labels;
  // historical call_logs.disposition_id FKs to disabled rows still resolve.
  const allDisps = useMemo(
    () => (store.callDispositions || []).filter(d => d.enabled !== false),
    [store.callDispositions]
  );

  // ── Campaign State (Supabase-backed, localStorage cache) ──
  const campaignKey = "cf_campaigns_" + (auth.currentUser?._orgId || "default");
  const [campaigns, setCampaignsRaw] = useState(() => {
    try { return JSON.parse(localStorage.getItem(campaignKey)) || []; } catch { return []; }
  });
  const [campaignsLoaded, setCampaignsLoaded] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [activeCampaignId, setActiveCampaignId] = useState(null);
  const [campaignDialIdx, setCampaignDialIdx] = useState(0);
  const [newCamp, setNewCamp] = useState({ name: "", maxAttempts: 6, cooldownHours: 24, callWindowStart: 9, callWindowEnd: 17, contactIds: [], steps: [{ type: "call", delay: 0 }, { type: "call", delay: 48 }] });

  // ── Refs ──
  const currentContactRef = useRef(null);
  const contactsRef = useRef([]);
  const authUserRef = useRef(null);
  const lastLoggedCallRef = useRef(null);
  // Tracks the call_logs.id from the most recent logCallToDb response so
  // handleDisposition can UPDATE that row's disposition_id when the user
  // picks a disposition. Set in the onEnded handler below; read in
  // handleDisposition. Cleared when a new call starts.
  const lastCallLogIdRef = useRef(null);
  contactsRef.current = contacts;
  authUserRef.current = auth.currentUser;
  // Match the contactsRef idiom above — synchronous direct-assignment per
  // render keeps refs aligned with current state without scheduling extra
  // effect commits. autoDialNext reads these so the post-countdown dial
  // targets the correct (post-removal) contact, not the closure snapshot
  // captured when startAutoDialCountdown was called.
  callQueueRef.current = callQueue;
  qiRef.current = qi;

  // ── Derived: Current Contact ──
  // In powerMode the queue is the source of truth — callQueue[qi] takes
  // priority over selectedContact so the auto-advance path can update the
  // displayed contact by mutating qi/callQueue alone. Outside powerMode
  // (manual entry, click-from-record) selectedContact still wins.
  // Manual queue-click during powerMode still works because the click sets
  // setQi(i) AND setSelectedContact(c); inside powerMode the useMemo reads
  // callQueue[qi] (which the click also updates), so the click target
  // displays correctly.
  const activeContact = useMemo(() => {
    if (powerMode && callQueue[qi]) return contacts.find(x => x.id === callQueue[qi].ct) || null;
    if (selectedContact) return selectedContact;
    // Search by manual number
    if (manualNumber && manualNumber.length >= 7) {
      const clean = manualNumber.replace(/[^\d]/g, "");
      return contacts.find(c => {
        const ph = (c.ph || c.phone || "").replace(/[^\d]/g, "");
        return ph && ph.length >= 7 && clean.endsWith(ph);
      }) || null;
    }
    return null;
  }, [selectedContact, powerMode, callQueue, qi, contacts, manualNumber]);

  currentContactRef.current = activeContact;

  // ── Contact Deals ──
  const contactDeals = useMemo(() => activeContact ? deals.filter(d => d.ct === activeContact.id) : [], [activeContact, deals]);

  // ── Contact Search Results ──
  const searchResults = useMemo(() => {
    if (!contactSearch || contactSearch.length < 2) return [];
    const q = contactSearch.toLowerCase();
    return contacts.filter(c =>
      (c.fn + " " + c.ln).toLowerCase().includes(q) ||
      (c.ph || "").includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.co || "").toLowerCase().includes(q)
    ).slice(0, 8);
  }, [contactSearch, contacts]);

  // ── Fetch call history for active contact ──
  useEffect(() => {
    if (!activeContact?.id) { setCallHistory([]); return; }
    supabase.from("call_logs").select("*").eq("contact_id", activeContact.id).order("started_at", { ascending: false }).limit(5)
      .then(({ data }) => { if (data) setCallHistory(data); });
  }, [activeContact?.id]);

  // ── Load campaigns & dispositions from Supabase on mount ──
  useEffect(() => {
    const orgId = auth.currentUser?._orgId;
    const userId = auth.currentUser?._dbId;
    if (!orgId) return;

    // (Removed: legacy organizations.custom_dispositions JSONB load. Dispositions
    // now flow through store.callDispositions, populated by the boot-time
    // orchestration in store.js against the call_dispositions table.)

    // Load campaigns from Supabase campaigns + campaign_entries tables
    (async () => {
      try {
        const { data: dbCampaigns, error } = await supabase.from("campaigns")
          .select("*").eq("org_id", orgId).order("created_at", { ascending: false });
        if (error) { console.warn("[CoreFlow] Campaigns load error:", error.message); return; }
        if (!dbCampaigns || dbCampaigns.length === 0) {
          // Check if there are localStorage campaigns to migrate
          const localCampaigns = JSON.parse(localStorage.getItem(campaignKey) || "[]");
          if (localCampaigns.length > 0) {
            console.log("[CoreFlow] Migrating", localCampaigns.length, "campaigns from localStorage to Supabase...");
            for (const lc of localCampaigns) {
              try {
                const { data: inserted } = await supabase.from("campaigns").insert({
                  org_id: orgId, created_by: userId, name: lc.name, status: lc.status || "active",
                  max_attempts: lc.maxAttempts || 6, cooldown_hours: lc.cooldownHours || 24,
                  call_window_start: lc.callWindowStart || 9, call_window_end: lc.callWindowEnd || 17,
                  steps: lc.steps || [], contact_ids: (lc.contactIds || []),
                }).select().single();
                if (inserted && lc.entries?.length > 0) {
                  const entries = lc.entries.map(e => ({
                    org_id: orgId, campaign_id: inserted.id, contact_id: e.contactId,
                    status: e.status || "pending", attempts: e.attempts || 0,
                    current_step: e.currentStep || 0, last_attempt_at: e.lastAttempt || null,
                    next_attempt_at: e.callbackAt || null,
                    metadata: { dispositions: e.dispositions || [] },
                  }));
                  await supabase.from("campaign_entries").insert(entries);
                }
              } catch (e) { console.warn("[CoreFlow] Campaign migration failed for:", lc.name, e.message); }
            }
            toast?.success("Campaigns migrated to cloud storage");
          }
          setCampaignsLoaded(true);
          // Re-load after migration
          const { data: reloaded } = await supabase.from("campaigns")
            .select("*").eq("org_id", orgId).order("created_at", { ascending: false });
          if (reloaded) {
            const mapped = await mapCampaignsFromDb(reloaded);
            setCampaignsRaw(mapped);
            try { localStorage.setItem(campaignKey, JSON.stringify(mapped)); } catch {}
          }
          return;
        }
        // Map DB campaigns to local shape
        const mapped = await mapCampaignsFromDb(dbCampaigns);
        setCampaignsRaw(mapped);
        try { localStorage.setItem(campaignKey, JSON.stringify(mapped)); } catch {}
        setCampaignsLoaded(true);
        console.log("[CoreFlow] Campaigns loaded from DB:", mapped.length);
      } catch (e) { console.warn("[CoreFlow] Campaigns load failed:", e.message); }
    })();

    async function mapCampaignsFromDb(dbCampaigns) {
      // Load all entries for these campaigns in one query
      const campaignIds = dbCampaigns.map(c => c.id);
      const { data: allEntries } = await supabase.from("campaign_entries")
        .select("*").in("campaign_id", campaignIds);
      const entriesByCampaign = {};
      (allEntries || []).forEach(e => {
        if (!entriesByCampaign[e.campaign_id]) entriesByCampaign[e.campaign_id] = [];
        entriesByCampaign[e.campaign_id].push({
          contactId: e.contact_id, status: e.status, attempts: e.attempts || 0,
          currentStep: e.current_step || 0, lastAttempt: e.last_attempt_at,
          callbackAt: e.next_attempt_at, dispositions: e.metadata?.dispositions || [],
          _dbId: e.id,
        });
      });
      return dbCampaigns.map(c => ({
        id: c.id, _dbId: c.id, name: c.name, status: c.status || "active",
        maxAttempts: c.max_attempts || 6, cooldownHours: c.cooldown_hours || 24,
        callWindowStart: c.call_window_start || 9, callWindowEnd: c.call_window_end || 17,
        steps: c.steps || [], contactIds: c.contact_ids || [],
        created: c.created_at ? new Date(c.created_at).toISOString().slice(0, 10) : "",
        entries: entriesByCampaign[c.id] || [],
      }));
    }
  }, [auth.currentUser?._orgId, auth.currentUser?._dbId]);

  // ── Sync campaigns to localStorage as cache whenever they change ──
  useEffect(() => {
    if (campaignsLoaded) {
      try { localStorage.setItem(campaignKey, JSON.stringify(campaigns)); } catch {}
    }
  }, [campaigns, campaignsLoaded]);

  // ── Retry failed logs ──
  useEffect(() => { retryFailedLogs(); }, []);

  // ── Pre-Call Intel ──
  const fetchPreCallIntel = useCallback(async (contact) => {
    if (!contact?.id) return;
    if (preCallContactRef.current === contact.id) return;
    preCallContactRef.current = contact.id;
    setPreCallLoading(true); setPreCallIntel(null);
    try {
      const contactDeals = store.deals?.filter(d => d.ct === contact.id) || [];
      const resp = await apiFetch("/api/ai/specialty", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "precall_intel", orgId: window.__coreflow_org_id, contact, activities: contact.activities || [], deals: contactDeals }) });
      const data = await resp.json();
      if (data.success && data.intel) setPreCallIntel(data.intel);
    } catch (e) { console.warn("[CoreFlow] Pre-call intel failed:", e.message); }
    finally { setPreCallLoading(false); }
  }, [store.deals]);

  useEffect(() => { if (activeContact && !calling) fetchPreCallIntel(activeContact); if (!activeContact) { setPreCallIntel(null); preCallContactRef.current = null; } }, [activeContact?.id, calling, fetchPreCallIntel]);

  // ── Post-Call Summary ──
  async function fetchPostCallSummary(contact, duration, disposition, notes) {
    if (!contact) return;
    setPostCallLoading(true);
    try {
      const resp = await apiFetch("/api/ai/specialty", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "postcall_summary", orgId: window.__coreflow_org_id, contact, deal: contactDeals[0] || null, duration, disposition, notes, activities: contact.activities || [] }) });
      const data = await resp.json();
      if (data.success && data.postcall) setPostCallSummary(data.postcall);
    } catch (e) { console.warn("[CoreFlow] Post-call summary failed:", e.message); }
    finally { setPostCallLoading(false); }
  }

  // ── Twilio Events ──
  // Register handlers ONCE on mount, return offCallEvent cleanup on
  // unmount. Previous version had no cleanup — every navigation to
  // /dialer added a fresh listener generation to the global twilio
  // singleton without removing old ones, causing N duplicate
  // call_logs INSERTs per call (one per generation). All state reads
  // inside handlers go through refs (contactsRef, authUserRef,
  // lastLoggedCallRef, preCallContactRef) so the [] deps boundary
  // doesn't strand them. The auth.currentUser fallback at onEnded
  // captures from first-mount closure (matches prior behavior).
  useEffect(() => {
    const onReady = () => { setDialerReady(true); setDialerStatus("Ready"); setDialerHealth("connected"); };
    const onError = (e) => { setDialerStatus("Error: " + (e?.message || "unknown")); };
    const onConnected = () => { setCalling(true); setDialing(false); setDialerStatus("Connected"); setIncomingCall(null); };
    const onHealth = (e) => { setDialerHealth(e.status); };
    const onEnded = (e) => {
      setCalling(false); setDialing(false); setDialerStatus("Ready"); setMuted(false); setIncomingCall(null);
      setPreCallIntel(null); preCallContactRef.current = null;
      const callKey = (e?.twilioSid || "") + "-" + (e?.to || "") + "-" + (e?.duration || 0);
      if (lastLoggedCallRef.current === callKey) return;
      lastLoggedCallRef.current = callKey;
      let contact = null;
      if (e?.to) {
        const cleanTo = (e.to || "").replace(/[^\d]/g, "");
        contact = contactsRef.current.find(c => { const cleanPh = (c.ph || c.phone || "").replace(/[^\d]/g, ""); return cleanPh && cleanPh.length >= 7 && cleanTo.endsWith(cleanPh); });
      }
      logCallToDb({ contactId: contact?.id || null, userId: authUserRef.current?._dbId || null, orgId: contact?._orgId || auth.currentUser?._orgId, phoneNumber: e?.to || "", duration: e?.duration || 0, status: (e?.duration || 0) > 0 ? "completed" : "no_answer", twilioSid: e?.twilioSid || null })
        .then(r => {
          if (r.success) {
            console.log("[CoreFlow] Call logged:", r.callLogId);
            // Stash the call_logs.id so handleDisposition can UPDATE this
            // row's disposition_id when the user picks a disposition. Cleared
            // by handleDisposition after use, and overwritten by the next
            // call's onEnded fire.
            lastCallLogIdRef.current = r.callLogId || null;
          }
        })
        .catch(err => console.warn("[CoreFlow] Call log error:", err));
      // Show disposition
      setDisp("sel");
    };
    const onDialing = () => { setDialing(true); setDialerStatus("Dialing..."); };
    const onRinging = () => { setDialing(true); setDialerStatus("Ringing..."); };
    const onMute = (e) => { setMuted(e.muted); };
    const onIncoming = (e) => { setIncomingCall({ from: e.from }); setDialerStatus("Incoming call..."); };
    const onRejected = () => { setIncomingCall(null); setDialerStatus("Ready"); };
    // Mid-call errors (activeCall.on("error")) used to fire into a
    // dead channel — no listener was registered anywhere. We mirror
    // onEnded's logging path with status:"failed", and use a
    // "-failed" callKey suffix so dedup doesn't collapse this with
    // a subsequent onEnded for the same SID (both should log).
    const onCallError = (e) => {
      setCalling(false); setDialing(false); setDialerStatus("Ready"); setMuted(false); setIncomingCall(null);
      setPreCallIntel(null); preCallContactRef.current = null;
      const callKey = (e?.twilioSid || "") + "-" + (e?.to || "") + "-" + (e?.duration || 0) + "-failed";
      if (lastLoggedCallRef.current === callKey) return;
      lastLoggedCallRef.current = callKey;
      let contact = null;
      if (e?.to) {
        const cleanTo = (e.to || "").replace(/[^\d]/g, "");
        contact = contactsRef.current.find(c => { const cleanPh = (c.ph || c.phone || "").replace(/[^\d]/g, ""); return cleanPh && cleanPh.length >= 7 && cleanTo.endsWith(cleanPh); });
      }
      logCallToDb({ contactId: contact?.id || null, userId: authUserRef.current?._dbId || null, orgId: contact?._orgId || auth.currentUser?._orgId, phoneNumber: e?.to || "", duration: e?.duration || 0, status: "failed", twilioSid: e?.twilioSid || null })
        .then(r => { if (r.success) console.log("[CoreFlow] Failed call logged:", r.callLogId); })
        .catch(err => console.warn("[CoreFlow] Failed-call log error:", err));
    };

    onCallEvent("ready", onReady);
    onCallEvent("error", onError);
    onCallEvent("connected", onConnected);
    onCallEvent("health", onHealth);
    onCallEvent("ended", onEnded);
    onCallEvent("dialing", onDialing);
    onCallEvent("ringing", onRinging);
    onCallEvent("mute", onMute);
    onCallEvent("incoming", onIncoming);
    onCallEvent("rejected", onRejected);
    onCallEvent("callError", onCallError);

    const checkReady = () => { const status = getStatus(); if (status.deviceReady) { setDialerReady(true); setDialerStatus("Ready"); } else setTimeout(checkReady, 500); };
    checkReady();

    return () => {
      offCallEvent("ready", onReady);
      offCallEvent("error", onError);
      offCallEvent("connected", onConnected);
      offCallEvent("health", onHealth);
      offCallEvent("ended", onEnded);
      offCallEvent("dialing", onDialing);
      offCallEvent("ringing", onRinging);
      offCallEvent("mute", onMute);
      offCallEvent("incoming", onIncoming);
      offCallEvent("rejected", onRejected);
      offCallEvent("callError", onCallError);
    };
  }, []);

  // ── Auto-dial from voice command ──
  useEffect(() => {
    const ad = window.__coreflow_auto_dial;
    if (ad) { window.__coreflow_auto_dial = null; if (ad.phone) { setManualNumber(ad.phone); const tryCall = () => { if (dialerReady) handleStartCall(ad.phone); else setTimeout(tryCall, 500); }; setTimeout(tryCall, 300); } }
  }, [dialerReady]);

  // ── Timer ──
  useEffect(() => { let t; if (calling) t = setInterval(() => setTime(x => x + 1), 1000); return () => clearInterval(t); }, [calling]);
  const fmt = (s) => String(Math.floor(s / 60)).padStart(2, "0") + ":" + String(s % 60).padStart(2, "0");

  // ── Org Numbers / Caller ID ──
  useEffect(() => {
    const orgId = auth.currentUser?._orgId;
    if (!orgId) return;
    apiFetch("/api/twilio/numbers", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "list", orgId }) })
      .then(r => r.json()).then(data => { if (data.numbers) setOrgNumbers(data.numbers); }).catch(() => {});
  }, [auth.currentUser?._orgId]);

  const userAssignedNumber = useMemo(() => {
    const userId = auth.currentUser?._dbId;
    if (!userId || orgNumbers.length === 0) return null;
    return orgNumbers.find(n => n.assigned_to === userId)?.phone_number || null;
  }, [orgNumbers, auth.currentUser?._dbId]);

  useEffect(() => {
    if (selectedCallerId) return;
    const dialingNum = manualNumber || activeContact?.ph || activeContact?.phone || "";
    if (!dialingNum || dialingNum.length < 6 || orgNumbers.length === 0) {
      setCallerIdPreview(userAssignedNumber ? { number: userAssignedNumber, match: "assigned" } : null);
      return;
    }
    const clean = dialingNum.replace(/[^\d]/g, "");
    let targetAC = clean.startsWith("1") && clean.length >= 4 ? clean.substring(1, 4) : clean.length >= 3 ? clean.substring(0, 3) : "";
    const getAC = (num) => { const c = (num || "").replace(/[^\d]/g, ""); return c.startsWith("1") && c.length >= 4 ? c.substring(1, 4) : c.length >= 3 ? c.substring(0, 3) : ""; };
    if (localPresence && targetAC) {
      const lp = orgNumbers.find(n => n.number_type === "local_presence" && getAC(n.phone_number) === targetAC);
      if (lp) { setCallerIdPreview({ number: lp.phone_number, match: "local" }); return; }
    }
    if (userAssignedNumber) setCallerIdPreview({ number: userAssignedNumber, match: "assigned" });
    else { const p = orgNumbers.find(n => n.is_primary); setCallerIdPreview(p ? { number: p.phone_number, match: "primary" } : orgNumbers[0] ? { number: orgNumbers[0].phone_number, match: "default" } : null); }
  }, [manualNumber, activeContact, orgNumbers, selectedCallerId, userAssignedNumber, localPresence]);

  // ── Call Actions ──
  const handleStartCall = (phoneNumber) => {
    if (!dialerReady) { setDialerStatus("Initialize dialer first"); return; }
    const num = phoneNumber || manualNumber || activeContact?.ph || activeContact?.phone;
    if (!num) { setDialerStatus("No phone number"); return; }
    setTime(0); setDisp(null);
    window.__coreflow_selected_callerid = selectedCallerId || callerIdPreview?.number || null;
    makeCall(num);
  };

  const handleHangUp = () => { hangUp(); setCalling(false); };
  const handleToggleMute = () => { const r = toggleMute(); setMuted(r?.muted || false); };

  // disposition is the full row from store.callDispositions (object with
  // id, label, behavior, requeue_days, etc). Picker passes the object via
  // d below; handleCampaignDisposition receives just the label string for
  // back-compat with its existing string-comparison business logic.
  const handleDisposition = (disposition) => {
    const label = disposition?.label || String(disposition || "");

    // ── SPECIAL CASE: behavior === "redial" ──
    // Redial is an action, not an outcome. Log the call + persist the
    // disposition_id on the just-ended call_logs row (so it shows in the
    // Call Logs page), then immediately redial the same number.
    //
    // Critically, we SKIP:
    //   • applyDisposition — no contact-side flag changes
    //     (do_not_call / qualified_at / requeue_after stay untouched)
    //   • removeFromQueue — contact stays at the same queue position
    //   • startAutoDialCountdown — no auto-advance to the next contact
    //
    // After the redialed call's eventual disposition, the normal flow
    // resumes and that disposition triggers queue advancement.
    if (disposition?.behavior === "redial") {
      setSessionStats(prev => ({ calls: prev.calls + 1, totalTime: prev.totalTime + time }));
      if (activeContact) {
        logCall({ contactId: activeContact.id, title: label + " — " + activeContact.fn + " " + activeContact.ln, duration: time, disposition: label, notes: noteText });
      }
      // Persist disposition_id on the call_logs row so the Disposition
      // column on the Call Logs page shows "Redial" for that attempt.
      if (lastCallLogIdRef.current && disposition.id) {
        supabase
          .from("call_logs")
          .update({ disposition_id: disposition.id })
          .eq("id", lastCallLogIdRef.current)
          .then(({ error }) => { if (error) console.warn("[CoreFlow] redial disposition_id update failed:", error.message); });
      }
      lastCallLogIdRef.current = null;
      // Capture the phone number BEFORE handleStartCall flips state.
      const redialNumber = activeContact?.ph || activeContact?.phone || manualNumber;
      setNoteText(""); setTime(0);
      if (redialNumber) {
        // handleStartCall internally setDisp(null), calls makeCall, which
        // runs the device.calls preflight (twilio.js:308-336) — handles
        // the ~50-500ms SDK teardown window automatically.
        handleStartCall(redialNumber);
      } else {
        console.warn("[CoreFlow Dialer] redial requested but no phone number found");
        setDisp(null);
      }
      return;
    }

    setSessionStats(prev => ({ calls: prev.calls + 1, connected: prev.connected + (label === "Connected" ? 1 : 0), totalTime: prev.totalTime + time }));
    if (activeContact) {
      logCall({ contactId: activeContact.id, title: label + " — " + activeContact.fn + " " + activeContact.ln, duration: time, disposition: label, notes: noteText });
      fetchPostCallSummary(activeContact, time, label, noteText);
      // Persist disposition_id on call_logs + apply behavior to contact.
      // Best-effort: errors logged inside applyDisposition. The call_log
      // id ref is captured by onEnded after logCallToDb resolves; if
      // logCallToDb hasn't resolved yet (rare race), the FK update is
      // skipped but the contact-side update still runs.
      if (disposition?.id) {
        store.applyDisposition(activeContact.id, disposition, lastCallLogIdRef.current)
          .catch(err => console.warn("[CoreFlow] applyDisposition failed:", err?.message));
      }
      // Clear the ref so a subsequent disposition (e.g. user changes their
      // mind and picks again — though UI flow makes this rare) doesn't
      // re-update the same call_log row's FK.
      lastCallLogIdRef.current = null;
    }
    setDisp(null); setNoteText(""); setTime(0);
    if (powerMode) {
      const currentItem = callQueue[qi];
      if (currentItem) {
        store.removeFromQueue(currentItem.id);
        // qi clamp uses POST-removal length. After removeFromQueue the new
        // array length is callQueue.length - 1. The item that was at qi+1
        // shifts down to qi (becoming the next contact), so we keep qi
        // unchanged unless it was the last index — in which case clamp to
        // the new last index, or 0 if the queue is now empty.
        setQi(prev => {
          const newLength = callQueue.length - 1;
          if (newLength <= 0) return 0;
          return Math.min(prev, newLength - 1);
        });
        // Clear selectedContact so activeContact's useMemo (which prefers
        // callQueue[qi] in powerMode) doesn't leak the dispositioned
        // contact into non-powerMode paths if the user toggles powerMode
        // off mid-session. Inside powerMode the new useMemo priority
        // already routes around selectedContact.
        setSelectedContact(null);
      }
      // Only kick off the countdown when there's at least one more contact
      // AFTER removal. callQueue.length is the pre-removal value, so > 1
      // is the correct guard for "post-removal length >= 1".
      if (callQueue.length > 1) startAutoDialCountdown();
    }
  };

  const startAutoDialCountdown = () => {
    if (autoDialTimerRef.current) clearInterval(autoDialTimerRef.current);
    setAutoDialCountdown(autoDialDelay);
    autoDialTimerRef.current = setInterval(() => {
      setAutoDialCountdown(prev => {
        if (prev <= 1) { clearInterval(autoDialTimerRef.current); autoDialTimerRef.current = null; autoDialNext(); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const cancelAutoDialCountdown = () => { if (autoDialTimerRef.current) { clearInterval(autoDialTimerRef.current); autoDialTimerRef.current = null; } setAutoDialCountdown(0); };

  const autoDialNext = () => {
    // Read via refs, NOT closure. setInterval was scheduled inside
    // handleDisposition with the pre-removal closure; reading callQueue/qi
    // directly here would resolve to the just-dispositioned contact and
    // re-dial that number. Refs are kept current via the synchronous
    // direct-assignment block alongside contactsRef.
    const currentQueue = callQueueRef.current;
    const currentQi = qiRef.current;

    // Defense-in-depth eligibility skip. addToQueue already filters at
    // insert-time, but flags can change between queueing and dialing
    // (e.g. another rep dispositions a contact, or a requeue_after
    // window expires/begins mid-session). Skip ineligible contacts and
    // advance qi until we find one that can be called or run off the
    // end of the queue.
    const now = Date.now();
    const isEligible = (ct) => {
      if (!ct) return false;
      if (ct.do_not_call) return false;
      if (ct.qualified_at) return false;
      if (ct.requeue_after && new Date(ct.requeue_after).getTime() > now) return false;
      return true;
    };

    let idx = currentQi;
    while (idx < currentQueue.length) {
      const item = currentQueue[idx];
      const candidate = item ? contactsRef.current.find(x => x.id === item.ct) : null;
      if (isEligible(candidate) && (candidate.ph || candidate.phone)) {
        if (idx !== currentQi) setQi(idx);
        handleStartCall(candidate.ph || candidate.phone);
        return;
      }
      idx += 1;
    }
    // No eligible contact remaining — leave qi alone so the user sees
    // the current item still highlighted. Power Dialer will idle.
    console.log("[CoreFlow] autoDialNext: no eligible contact in queue from index", currentQi);
  };

  const startPowerDialer = () => {
    setPowerMode(true); setQueueOpen(true); setShowSessionConfig(false);
    if (callQueue.length > 0) {
      setQi(0);
      const firstContact = contacts.find(x => x.id === callQueue[0]?.ct);
      if (firstContact) setSelectedContact(firstContact);
    }
  };

  // ── Contact Field Config (customizable) ──
  const defaultFields = ["email", "ph", "co", "title", "source", "status", "city", "state", "score", "industry"];
  const [visibleFields] = useState(defaultFields);

  // ── Resizable Panels ──
  const [leftPanelWidth, setLeftPanelWidth] = useState(420);
  const dragRef = useRef(null);
  const startDrag = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = leftPanelWidth;
    const queueW = (powerMode && queueOpen) ? 260 : 0;
    const onMove = (ev) => {
      const delta = ev.clientX - startX;
      const newW = Math.max(300, Math.min(700, startW + delta));
      setLeftPanelWidth(newW);
    };
    const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const fieldLabels = { email: "Email", ph: "Phone", co: "Company", title: "Title", source: "Source", status: "Status", city: "City", state: "State", score: "Score", industry: "Industry", tags: "Tags" };

  // ── Campaign Functions (Supabase-backed) ──
  const createCampaign = async () => {
    if (!newCamp.name.trim()) return;
    const orgId = auth.currentUser?._orgId;
    const userId = auth.currentUser?._dbId;

    // Build local campaign shape for instant UI update
    const localCampaign = {
      id: Date.now(), ...newCamp, status: "active", created: new Date().toISOString().slice(0, 10),
      entries: newCamp.contactIds.map(cid => ({ contactId: cid, attempts: 0, currentStep: 0, status: "pending", lastAttempt: null, callbackAt: null, dispositions: [] })),
    };

    // Instant UI update
    setCampaignsRaw(prev => [...prev, localCampaign]);
    setNewCamp({ name: "", maxAttempts: 6, cooldownHours: 24, callWindowStart: 9, callWindowEnd: 17, contactIds: [], steps: [{ type: "call", delay: 0 }, { type: "call", delay: 48 }] });
    setShowNewCampaign(false);

    // Persist to Supabase
    if (orgId) {
      try {
        const { data: inserted, error } = await supabase.from("campaigns").insert({
          org_id: orgId, created_by: userId, name: newCamp.name, status: "active",
          max_attempts: newCamp.maxAttempts || 6, cooldown_hours: newCamp.cooldownHours || 24,
          call_window_start: newCamp.callWindowStart || 9, call_window_end: newCamp.callWindowEnd || 17,
          steps: newCamp.steps || [], contact_ids: newCamp.contactIds || [],
        }).select().single();
        if (error) throw error;
        // Insert campaign entries
        if (inserted && newCamp.contactIds?.length > 0) {
          const entries = newCamp.contactIds.map(cid => ({
            org_id: orgId, campaign_id: inserted.id, contact_id: cid,
            status: "pending", attempts: 0, current_step: 0,
          }));
          await supabase.from("campaign_entries").insert(entries);
        }
        // Update local campaign with real DB id
        setCampaignsRaw(prev => prev.map(c => c.id === localCampaign.id ? { ...c, id: inserted.id, _dbId: inserted.id } : c));
        console.log("[CoreFlow] Campaign created in DB:", inserted.id);
      } catch (e) {
        console.error("[CoreFlow] Campaign save failed:", e.message);
        toast?.error("Campaign created locally but failed to save to cloud — it may be lost on refresh");
      }
    }
  };

  const toggleCampaignStatus = async (id) => {
    const camp = campaigns.find(c => c.id === id);
    if (!camp) return;
    const newStatus = camp.status === "active" ? "paused" : "active";
    // Instant UI update
    setCampaignsRaw(prev => prev.map(c => c.id === id ? { ...c, status: newStatus } : c));
    // Persist to Supabase
    const dbId = camp._dbId || id;
    const { error } = await supabase.from("campaigns").update({ status: newStatus }).eq("id", dbId);
    if (error) {
      console.error("[CoreFlow] Campaign status update failed:", error.message);
      toast?.error("Failed to update campaign status");
    }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    const camp = campaigns.find(c => c.id === id);
    // Instant UI update
    setCampaignsRaw(prev => prev.filter(c => c.id !== id));
    // Persist to Supabase (entries cascade-delete via FK)
    const dbId = camp?._dbId || id;
    const { error } = await supabase.from("campaigns").delete().eq("id", dbId);
    if (error) {
      console.error("[CoreFlow] Campaign delete failed:", error.message);
      toast?.error("Failed to delete campaign from cloud");
    }
  };

  const startCampaignDial = (campaignId) => {
    const camp = campaigns.find(c => c.id === campaignId);
    if (!camp) return;
    const idx = (camp.entries || []).findIndex(e => canDial(e, camp));
    if (idx < 0) return;
    setActiveCampaignId(campaignId);
    setCampaignDialIdx(idx);
    const entry = camp.entries[idx];
    const ct = contacts.find(c => c.id === entry.contactId);
    if (ct) setSelectedContact(ct);
    setShowCampaigns(false);
  };

  const handleCampaignDisposition = (label) => {
    const camp = campaigns.find(c => c.id === activeCampaignId);
    if (!camp) return;
    const entry = (camp.entries || [])[campaignDialIdx];
    if (!entry) return;
    const ct = contacts.find(c => c.id === entry.contactId);
    const updatedEntry = { ...entry, attempts: entry.attempts + 1, lastAttempt: new Date().toISOString(), dispositions: [...entry.dispositions, { label, date: new Date().toISOString(), duration: time }] };
    if (label === "Connected") { updatedEntry.status = "reached"; updatedEntry.currentStep = (entry.currentStep || 0) + 1; }
    else if (label === "Not Interested") { updatedEntry.status = "removed"; }
    else if (label === "Callback") { updatedEntry.callbackAt = new Date(Date.now() + 24 * 3600000).toISOString(); }
    else { updatedEntry.currentStep = (entry.currentStep || 0) + 1; if (updatedEntry.attempts >= camp.maxAttempts) updatedEntry.status = "exhausted"; }
    if (ct) logCall({ contactId: ct.id, title: label + " — " + ct.fn + " " + ct.ln + " (Campaign: " + camp.name + ")", duration: time, disposition: label, notes: noteText });
    setSessionStats(prev => ({ calls: prev.calls + 1, connected: prev.connected + (label === "Connected" ? 1 : 0), totalTime: prev.totalTime + time }));
    setCampaignsRaw(prev => prev.map(c => c.id === activeCampaignId ? { ...c, entries: (c.entries || []).map((e, i) => i === campaignDialIdx ? updatedEntry : e) } : c));
    // Persist entry update to Supabase
    if (entry._dbId) {
      supabase.from("campaign_entries").update({
        status: updatedEntry.status, attempts: updatedEntry.attempts,
        current_step: updatedEntry.currentStep, last_attempt_at: updatedEntry.lastAttempt,
        next_attempt_at: updatedEntry.callbackAt || null,
        metadata: { dispositions: updatedEntry.dispositions },
      }).eq("id", entry._dbId).then(({ error }) => {
        if (error) console.error("[CoreFlow] Campaign entry update failed:", error.message);
      });
    }
    setNoteText(""); setDisp(null); setTime(0);
    // Advance to next dialable
    const nextIdx = (camp.entries || []).findIndex((e, i) => i > campaignDialIdx && canDial(e, camp));
    if (nextIdx >= 0) {
      setCampaignDialIdx(nextIdx);
      const nextCt = contacts.find(c => c.id === camp.entries[nextIdx].contactId);
      if (nextCt) setSelectedContact(nextCt);
      startAutoDialCountdown();
    } else {
      setActiveCampaignId(null);
    }
  };

  const activeCampaign = campaigns.find(c => c.id === activeCampaignId);
  const activeEntry = activeCampaign?.entries?.[campaignDialIdx];

  // ── RENDER ──
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, overflow: isMobile ? "auto" : "hidden", WebkitOverflowScrolling: "touch" }}>
      <style>{`
        .cf-dialer-search input:focus, .cf-dialer-search input:focus-visible { outline: none !important; box-shadow: none !important; border: none !important; }
      `}</style>

      {/* ═══ TOP BAR: Search + Mode Toggle + Settings ═══ */}
      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 10, padding: isMobile ? "10px 12px" : "12px 20px", borderBottom: "1px solid rgba(217,219,221,0.08)", flexShrink: 0, background: "#2C2E33", overflowX: isMobile ? "auto" : "visible", WebkitOverflowScrolling: "touch", flexWrap: isMobile ? "wrap" : "nowrap" }}>
        {/* Search / Number Input */}
        <div style={{ position: "relative", flex: isMobile ? "1 1 100%" : 1, maxWidth: isMobile ? "100%" : 400 }}>
          <div className="cf-dialer-search" style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.05)", transition: "border-color 0.15s, box-shadow 0.15s", outline: "none" }}
            tabIndex={-1}
            onFocusCapture={e => { e.currentTarget.style.borderColor = "rgba(64,89,173,0.5)"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(64,89,173,0.15)"; }}
            onBlurCapture={e => { if (!e.currentTarget.contains(e.relatedTarget)) { e.currentTarget.style.borderColor = "rgba(217,219,221,0.12)"; e.currentTarget.style.boxShadow = "none"; } }}>
            <Icon name="search" size={14} color={T.tx3} />
            <input
              value={powerMode ? "" : (contactSearch || manualNumber)}
              onChange={e => {
                const v = e.target.value;
                if (/^[+\d\s()-]*$/.test(v)) { setManualNumber(v); setContactSearch(""); }
                else { setContactSearch(v); setManualNumber(""); }
              }}
              placeholder={powerMode ? "Power Dialer Active" : "Search contacts or enter number..."}
              disabled={powerMode}
              style={{ border: "none", outline: "none", background: "transparent", color: "#FAFAFA", fontSize: 13, width: "100%", fontFamily: "inherit" }}
            />
            {(contactSearch || manualNumber) && !powerMode && (
              <button onClick={() => { setContactSearch(""); setManualNumber(""); setSelectedContact(null); }} style={{ background: "none", border: "none", color: T.tx3, cursor: "pointer", fontSize: 14 }}>×</button>
            )}
          </div>
          {/* Search Dropdown */}
          {searchResults.length > 0 && !powerMode && (
            <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#33363D", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, boxShadow: "0 24px 64px rgba(0,0,0,0.6)", zIndex: 100, maxHeight: 300, overflowY: "auto" }}>
              {searchResults.map(c => (
                <div key={c.id} onClick={() => { setSelectedContact(c); setContactSearch(""); setManualNumber(c.ph || c.phone || ""); }}
                  style={{ padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid rgba(217,219,221,0.06)" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <div style={{ fontSize: 12, color: "#FAFAFA" }}>{c.fn} {c.ln}</div>
                  <div style={{ fontSize: 10, color: T.tx3 }}>{c.co} · {c.ph || c.phone || "No phone"}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Power Dialer Toggle */}
        {!powerMode ? (
          <button onClick={() => { if (callQueue.length > 0) setShowSessionConfig(true); else setShowSessionConfig(true); }}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
            <Icon name="phone" size={12} color="#FAFAFA" /> Power Dialer {callQueue.length > 0 && <span style={{ fontSize: 9, opacity: 0.5 }}>({callQueue.length})</span>}
          </button>
        ) : (
          <button onClick={() => { setPowerMode(false); cancelAutoDialCountdown(); }}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(64,89,173,0.15)", color: "#FAFAFA", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
            Exit Power Dialer
          </button>
        )}

        {/* Queue Toggle (power mode) */}
        {powerMode && (
          <button onClick={() => setQueueOpen(!queueOpen)}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: queueOpen ? "rgba(255,255,255,0.06)" : "transparent", color: "#FAFAFA", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
            Queue ({callQueue.length})
          </button>
        )}

        {/* Campaigns */}
        <button onClick={() => setShowCampaigns(!showCampaigns)}
          style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: showCampaigns ? "rgba(255,255,255,0.06)" : "transparent", color: "#FAFAFA", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
          Campaigns
        </button>

        {/* Session Stats */}
        {powerMode && (
          <div style={{ display: "flex", gap: 12, marginLeft: "auto" }}>
            {[{ l: "Calls", v: sessionStats.calls }, { l: "Connected", v: sessionStats.connected }, { l: "Rate", v: sessionStats.calls > 0 ? Math.round(sessionStats.connected / sessionStats.calls * 100) + "%" : "0%" }].map(s => (
              <div key={s.l} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 8, color: T.tx3, letterSpacing: ".06em" }}>{s.l}</div>
                <div style={{ fontSize: 13, color: "#FAFAFA" }}>{s.v}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ═══ MAIN AREA ═══ */}
      <div style={{ flex: 1, display: "flex", flexDirection: isMobile ? "column" : "row", overflow: isMobile ? "auto" : "hidden", WebkitOverflowScrolling: "touch" }}>

        {/* ═══ CAMPAIGNS PANEL ═══ */}
        {showCampaigns ? (
          <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 16, color: "#FAFAFA" }}>Campaigns</div>
                <div style={{ fontSize: 11, color: T.tx3 }}>{campaigns.length} campaign{campaigns.length !== 1 ? "s" : ""}</div>
              </div>
              <button onClick={() => setShowNewCampaign(!showNewCampaign)}
                style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
                + New Campaign
              </button>
            </div>

            {/* New Campaign Form */}
            {showNewCampaign && (
              <div style={{ ...crd, padding: 20, marginBottom: 16, borderLeft: "3px solid #4059AD" }}>
                <div style={{ fontSize: 13, color: "#FAFAFA", marginBottom: 14 }}>Create Campaign</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
                  <div style={{ flex: "1 1 200px" }}>
                    <div style={lbl}>Name</div>
                    <input value={newCamp.name} onChange={e => setNewCamp(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Q1 Outbound"
                      style={{ ...inp, width: "100%", boxSizing: "border-box" }} />
                  </div>
                  <div>
                    <div style={lbl}>Max Attempts</div>
                    <input type="number" value={newCamp.maxAttempts} onChange={e => setNewCamp(p => ({ ...p, maxAttempts: Number(e.target.value) }))}
                      style={{ ...inp, width: 70 }} />
                  </div>
                  <div>
                    <div style={lbl}>Cooldown (hrs)</div>
                    <input type="number" value={newCamp.cooldownHours} onChange={e => setNewCamp(p => ({ ...p, cooldownHours: Number(e.target.value) }))}
                      style={{ ...inp, width: 70 }} />
                  </div>
                  <div>
                    <div style={lbl}>Call Window</div>
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <input type="number" value={newCamp.callWindowStart} onChange={e => setNewCamp(p => ({ ...p, callWindowStart: Number(e.target.value) }))} style={{ ...inp, width: 48, textAlign: "center" }} />
                      <span style={{ fontSize: 10, color: T.tx3 }}>to</span>
                      <input type="number" value={newCamp.callWindowEnd} onChange={e => setNewCamp(p => ({ ...p, callWindowEnd: Number(e.target.value) }))} style={{ ...inp, width: 48, textAlign: "center" }} />
                    </div>
                  </div>
                </div>

                {/* Sequence Steps */}
                <div style={{ marginBottom: 14 }}>
                  <div style={lbl}>SEQUENCE STEPS</div>
                  {(newCamp.steps || []).map((step, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <div style={{ width: 20, height: 20, borderRadius: 10, background: "rgba(64,89,173,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#FAFAFA", flexShrink: 0 }}>{i + 1}</div>
                      <select value={step.type} onChange={e => { const s = [...newCamp.steps]; s[i] = { ...s[i], type: e.target.value }; setNewCamp(p => ({ ...p, steps: s })); }}
                        style={{ ...inp, width: 100 }}>
                        <option value="call">Call</option><option value="email">Email</option>
                      </select>
                      {i > 0 && (
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <span style={{ fontSize: 10, color: T.tx3 }}>wait</span>
                          <input type="number" value={step.delay} onChange={e => { const s = [...newCamp.steps]; s[i] = { ...s[i], delay: Number(e.target.value) }; setNewCamp(p => ({ ...p, steps: s })); }}
                            style={{ ...inp, width: 48, textAlign: "center" }} />
                          <span style={{ fontSize: 10, color: T.tx3 }}>hrs</span>
                        </div>
                      )}
                      <button onClick={() => setNewCamp(p => ({ ...p, steps: p.steps.filter((_, j) => j !== i) }))}
                        style={{ background: "none", border: "none", color: T.tx3, cursor: "pointer", fontSize: 14 }}>×</button>
                    </div>
                  ))}
                  <button onClick={() => setNewCamp(p => ({ ...p, steps: [...p.steps, { type: "call", delay: 24 }] }))}
                    style={{ fontSize: 10, color: "#FAFAFA", background: "none", border: "none", cursor: "pointer" }}>+ Add Step</button>
                </div>

                {/* Contact Selection */}
                <div style={{ marginBottom: 14 }}>
                  <div style={lbl}>SELECT CONTACTS ({newCamp.contactIds.length})</div>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap", maxHeight: 200, overflowY: "auto" }}>
                    {contacts.map(c => {
                      const sel = newCamp.contactIds.includes(c.id);
                      return (
                        <button key={c.id} onClick={() => setNewCamp(p => ({ ...p, contactIds: sel ? p.contactIds.filter(id => id !== c.id) : [...p.contactIds, c.id] }))}
                          style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid " + (sel ? "rgba(64,89,173,0.4)" : "rgba(217,219,221,0.08)"), background: sel ? "rgba(64,89,173,0.08)" : "transparent", color: sel ? "#FAFAFA" : T.tx3, fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>
                          {c.fn} {c.ln}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={createCampaign} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#4059AD", color: "#D9DBDD", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Create</button>
                  <button onClick={() => setShowNewCampaign(false)} style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: T.tx3, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Campaign Cards */}
            {campaigns.length === 0 && !showNewCampaign && (
              <div style={{ ...crd, padding: 60, textAlign: "center" }}>
                <div style={{ fontSize: 13, color: "#FAFAFA", marginBottom: 4 }}>No campaigns yet</div>
                <div style={{ fontSize: 11, color: T.tx3, marginBottom: 16 }}>Create multi-touch sequences with cadence rules</div>
                <button onClick={() => setShowNewCampaign(true)} style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>+ Create First Campaign</button>
              </div>
            )}

            {campaigns.map(camp => {
              const total = (camp.entries || []).length;
              const reached = (camp.entries || []).filter(e => e.status === "reached").length;
              const pending = (camp.entries || []).filter(e => e.status === "pending").length;
              const exhausted = (camp.entries || []).filter(e => e.status === "exhausted").length;
              const removed = (camp.entries || []).filter(e => e.status === "removed").length;
              const totalAttempts = (camp.entries || []).reduce((a, e) => a + e.attempts, 0);
              const totalConnected = (camp.entries || []).reduce((a, e) => a + e.dispositions.filter(d => d.label === "Connected").length, 0);
              const dialableCount = (camp.entries || []).filter(e => canDial(e, camp)).length;
              return (
                <div key={camp.id} style={{ ...crd, padding: 18, marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 14, color: "#FAFAFA" }}>{camp.name}</span>
                      <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 4, background: camp.status === "active" ? "rgba(46,139,87,0.1)" : "rgba(212,118,58,0.1)", color: camp.status === "active" ? "#2E8B57" : "#D4763A" }}>{camp.status}</span>
                      <span style={{ fontSize: 10, color: T.tx3 }}>{(camp.steps || []).length} steps · Max {camp.maxAttempts} · {camp.cooldownHours}h cooldown</span>
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      {camp.status === "active" && dialableCount > 0 && (
                        <button onClick={() => startCampaignDial(camp.id)}
                          style={{ padding: "6px 14px", borderRadius: 8, border: "none", background: "#4059AD", color: "#D9DBDD", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>
                          Dial ({dialableCount})
                        </button>
                      )}
                      <button onClick={() => toggleCampaignStatus(camp.id)} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>{camp.status === "active" ? "Pause" : "Resume"}</button>
                      <button onClick={() => deleteCampaign(camp.id)} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(139,69,87,0.3)", background: "transparent", color: "#E53935", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>Delete</button>
                    </div>
                  </div>
                  <ProgressBar value={reached} max={total || 1} color="#2E8B57" height={4} />
                  <div style={{ display: "flex", gap: 16, fontSize: 10, color: T.tx3, marginTop: 8 }}>
                    <span>Total: <span style={{ color: "#FAFAFA" }}>{total}</span></span>
                    <span>Reached: <span style={{ color: "#2E8B57" }}>{reached}</span></span>
                    <span>Pending: <span style={{ color: "#FAFAFA" }}>{pending}</span></span>
                    <span>Exhausted: <span style={{ color: "#D4763A" }}>{exhausted}</span></span>
                    <span>Removed: <span style={{ color: "#E53935" }}>{removed}</span></span>
                    <span>Connect: <span style={{ color: "#FAFAFA" }}>{totalAttempts > 0 ? Math.round(totalConnected / totalAttempts * 100) : 0}%</span></span>
                  </div>
                  {/* Entries */}
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(217,219,221,0.06)" }}>
                    {(camp.entries || []).map((entry, ei) => {
                      const c = contacts.find(x => x.id === entry.contactId);
                      if (!c) return null;
                      const dialable = canDial(entry, camp);
                      return (
                        <div key={ei} style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 6, background: dialable ? "rgba(46,139,87,0.06)" : "transparent", border: "1px solid " + (dialable ? "rgba(46,139,87,0.2)" : "rgba(217,219,221,0.06)"), fontSize: 10, color: "#FAFAFA" }}>
                          <div style={{ width: 5, height: 5, borderRadius: 3, background: entry.status === "reached" ? "#2E8B57" : entry.status === "exhausted" ? "#D4763A" : entry.status === "removed" ? "#E53935" : T.tx3 }} />
                          {c.fn} {c.ln?.[0]}.
                          <span style={{ color: T.tx3 }}>{entry.attempts}/{camp.maxAttempts}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (<>

        {/* ── Queue Drawer (collapsible left) ── */}
        {powerMode && queueOpen && (
          <div style={{ width: 260, borderRight: "1px solid rgba(217,219,221,0.08)", overflow: "auto", flexShrink: 0 }}>
            <div style={{ padding: "10px 12px", borderBottom: "1px solid rgba(217,219,221,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ ...lbl, marginBottom: 0 }}>QUEUE ({callQueue.length})</span>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {callQueue.length > 0 && (
                  <button
                    onClick={() => {
                      if (!window.confirm("Clear all " + callQueue.length + " contact" + (callQueue.length === 1 ? "" : "s") + " from the queue? This cannot be undone.")) return;
                      store.clearQueue();
                      setQi(0);
                      cancelAutoDialCountdown();
                      toast?.success("Queue cleared");
                    }}
                    style={{ background: "none", border: "1px solid rgba(217,219,221,0.12)", color: T.tx3, cursor: "pointer", fontSize: 9, padding: "3px 8px", borderRadius: 6, fontFamily: "inherit", letterSpacing: ".04em", textTransform: "uppercase" }}>
                    Clear All
                  </button>
                )}
                <button onClick={() => setQueueOpen(false)} style={{ background: "none", border: "none", color: T.tx3, cursor: "pointer", fontSize: 14 }}>×</button>
              </div>
            </div>
            {callQueue.map((q, i) => {
              const c = contacts.find(x => x.id === q.ct);
              return (
                <div key={q.id} onClick={() => { setQi(i); setSelectedContact(c); setDisp(null); setTime(0); }}
                  style={{ padding: "10px 12px", borderBottom: "1px solid rgba(217,219,221,0.06)", cursor: "pointer", background: i === qi ? "rgba(64,89,173,0.08)" : "transparent", borderLeft: i === qi ? "3px solid #4059AD" : "3px solid transparent" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ fontSize: 11, color: "#FAFAFA" }}>{c ? c.fn + " " + c.ln : "Unknown"}</div>
                      <div style={{ fontSize: 9, color: T.tx3 }}>{c?.co || ""}</div>
                    </div>
                    <button onClick={e => { e.stopPropagation(); store.removeFromQueue(q.id); if (i <= qi && qi > 0) setQi(p => p - 1); }}
                      style={{ background: "none", border: "none", color: T.tx3, cursor: "pointer", fontSize: 12 }}>×</button>
                  </div>
                </div>
              );
            })}
            {callQueue.length === 0 && <div style={{ padding: 20, textAlign: "center", color: T.tx3, fontSize: 11 }}>Queue is empty</div>}
          </div>
        )}

        {/* ── Main Content ── */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>

          {/* LEFT: Contact Card + Call Controls */}
          <div style={{ width: leftPanelWidth, minWidth: 300, maxWidth: 700, borderRight: "none", overflow: "auto", padding: 20, flexShrink: 0, height: "calc(100vh - 200px)", maxHeight: "calc(92vh - 110px)" }}>

            {activeContact ? (<>
              {/* Contact Header */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 20, color: "#FAFAFA" }}>{activeContact.fn} {activeContact.ln}</div>
                <div style={{ fontSize: 12, color: T.tx3, marginTop: 2 }}>{activeContact.title}{activeContact.co ? " — " + activeContact.co : ""}</div>
                <div style={{ fontSize: 13, color: "#4059AD", marginTop: 4 }}>{activeContact.ph || activeContact.phone || "No phone"}</div>
              </div>

              {/* Call Controls */}
              <div style={{ ...crd, padding: 16, marginBottom: 16 }}>
                {autoDialCountdown > 0 ? (
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 32, color: "#FAFAFA", marginBottom: 8 }}>{autoDialCountdown}</div>
                    <div style={{ fontSize: 10, color: T.tx3, marginBottom: 12 }}>Next call in {autoDialCountdown}s</div>
                    <button onClick={cancelAutoDialCountdown} style={{ padding: "6px 20px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: T.tx3, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                  </div>
                ) : calling ? (
                  <div>
                    <div style={{ textAlign: "center", marginBottom: 12 }}>
                      <div style={{ fontSize: 10, color: "#2E8B57", letterSpacing: ".08em", marginBottom: 4 }}>CONNECTED</div>
                      <div style={{ fontSize: 28, color: "#FAFAFA", fontVariantNumeric: "tabular-nums" }}>{fmt(time)}</div>
                    </div>
                    <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                      <button onClick={handleToggleMute} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: muted ? "rgba(139,69,87,0.15)" : "transparent", color: muted ? "#E53935" : "#FAFAFA", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>{muted ? "Unmute" : "Mute"}</button>
                      <button onClick={handleHangUp} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(139,69,87,0.3)", background: "rgba(139,69,87,0.15)", color: "#E53935", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>End</button>
                      <button onClick={() => setDisp("transfer")} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>Transfer</button>
                    </div>
                  </div>
                ) : disp === "sel" ? (
                  <div>
                    <div style={{ ...lbl, marginBottom: 8 }}>DISPOSITION</div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
                      {allDisps.map(d => (
                        <button key={d.id} onClick={() => activeCampaignId ? handleCampaignDisposition(d.label) : handleDisposition(d)}
                          style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}
                          onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.06)"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: dialing ? "#D4763A" : (dialerReady ? "#2E8B57" : T.tx3), marginBottom: 8 }}>{dialing ? "DIALING..." : (dialerReady ? "DIALER READY" : dialerStatus)}</div>
                    <button onClick={() => handleStartCall()} disabled={!dialerReady || dialing || calling}
                      style={{ padding: "12px 36px", borderRadius: 8, border: "none", background: (dialerReady && !dialing && !calling) ? "#4059AD" : "rgba(255,255,255,0.06)", color: (dialerReady && !dialing && !calling) ? "#D9DBDD" : T.tx3, fontSize: 13, cursor: (dialerReady && !dialing && !calling) ? "pointer" : "default", fontFamily: "inherit" }}>
                      Call {activeContact.fn}
                    </button>
                    {powerMode && <button onClick={() => { const item = callQueue[qi]; if (item) { store.removeFromQueue(item.id); setQi(i => Math.min(i, callQueue.length - 2)); } }} style={{ display: "block", margin: "8px auto 0", background: "none", border: "none", color: T.tx3, fontSize: 10, cursor: "pointer" }}>Skip</button>}
                  </div>
                )}

                {/* Caller ID / Settings Strip */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12, paddingTop: 10, borderTop: "1px solid rgba(217,219,221,0.06)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 9, color: T.tx3 }}>Calling as:</span>
                    <button onClick={() => setShowCallerIdPicker(!showCallerIdPicker)} style={{ fontSize: 10, color: "#FAFAFA", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                      {selectedCallerId || callerIdPreview?.number || "Default"} <span style={{ opacity: 0.4 }}>▾</span>
                    </button>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => setLocalPresence(!localPresence)} style={{ fontSize: 8, padding: "2px 6px", borderRadius: 4, border: "1px solid rgba(217,219,221,0.12)", background: localPresence ? "rgba(64,89,173,0.15)" : "transparent", color: localPresence ? "#FAFAFA" : T.tx3, cursor: "pointer", fontFamily: "inherit" }}>LP</button>
                    <button onClick={() => setCallRecording(!callRecording)} style={{ fontSize: 8, padding: "2px 6px", borderRadius: 4, border: "1px solid rgba(217,219,221,0.12)", background: callRecording ? "rgba(64,89,173,0.15)" : "transparent", color: callRecording ? "#FAFAFA" : T.tx3, cursor: "pointer", fontFamily: "inherit" }}>REC</button>
                    <button title="Live transcription coming soon — calls are automatically analyzed after they end" style={{ fontSize: 8, padding: "2px 6px", borderRadius: 4, border: "1px solid rgba(217,219,221,0.08)", background: "transparent", color: "rgba(217,219,221,0.25)", cursor: "default", fontFamily: "inherit" }}>LIVE</button>
                  </div>
                </div>
                {showCallerIdPicker && (
                  <div style={{ marginTop: 8, padding: 8, background: "#33363D", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)" }}>
                    {orgNumbers.map(n => (
                      <div key={n.phone_number} onClick={() => { setSelectedCallerId(n.phone_number); setShowCallerIdPicker(false); }}
                        style={{ padding: "6px 10px", borderRadius: 6, cursor: "pointer", fontSize: 11, color: "#FAFAFA" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.06)"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        {n.phone_number} {n.assigned_to === auth.currentUser?._dbId ? "(yours)" : ""} {n.number_type === "local_presence" ? "(LP)" : ""}
                      </div>
                    ))}
                    <div onClick={() => { setSelectedCallerId(null); setShowCallerIdPicker(false); }}
                      style={{ padding: "6px 10px", borderRadius: 6, cursor: "pointer", fontSize: 11, color: T.tx3 }}
                      onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.06)"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      Auto (default)
                    </div>
                  </div>
                )}
              </div>

              {/* Contact Fields Card */}
              <div style={{ ...crd, padding: 14, marginBottom: 12 }}>
                <div style={{ ...lbl, marginBottom: 8 }}>CONTACT DETAILS</div>
                {visibleFields.map(key => {
                  const val = activeContact[key] || "—";
                  return (
                    <div key={key} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 11, borderBottom: "1px solid rgba(217,219,221,0.04)", gap: 8 }}>
                      <span style={{ color: T.tx3, flexShrink: 0 }}>{fieldLabels[key] || key}</span>
                      <span style={{ color: "#FAFAFA", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, textAlign: "right" }}>{val}</span>
                    </div>
                  );
                })}
              </div>

              {/* Deal Card */}
              {contactDeals.length > 0 && (
                <div style={{ ...crd, padding: 14, marginBottom: 12 }}>
                  <div style={{ ...lbl, marginBottom: 8 }}>DEALS ({contactDeals.length})</div>
                  {contactDeals.map(d => (
                    <div key={d.id} style={{ padding: "8px 0", borderBottom: "1px solid rgba(217,219,221,0.04)" }}>
                      <div style={{ fontSize: 11, color: "#FAFAFA" }}>{d.name}</div>
                      <div style={{ fontSize: 10, color: T.tx3 }}>{d.stage} · ${d.val?.toLocaleString()} · {d.prob}%</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Recent Call History */}
              {callHistory.length > 0 && (
                <div style={{ ...crd, padding: 14 }}>
                  <div style={{ ...lbl, marginBottom: 8 }}>RECENT CALLS</div>
                  {callHistory.map((cl, i) => {
                    const recSid = cl.recording_url ? (cl.recording_url.match(/Recordings\/(RE[a-f0-9]+)/i) || [])[1] : null;
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid rgba(217,219,221,0.04)" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 10, color: "#FAFAFA" }}>{cl.outcome || cl.status || "No disposition"}</div>
                          <div style={{ fontSize: 9, color: T.tx3 }}>{cl.direction === "inbound" ? "Inbound" : "Outbound"} · {cl.duration ? Math.floor(cl.duration / 60) + ":" + String(cl.duration % 60).padStart(2, "0") : "0:00"} · {new Date(cl.started_at).toLocaleDateString()}</div>
                        </div>
                        {(recSid || cl.recording_url) && <PlayBtn url={cl.recording_url} />}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Small Numpad */}
              <details style={{ marginTop: 12 }}>
                <summary style={{ fontSize: 10, color: T.tx3, cursor: "pointer", userSelect: "none" }}>Numpad</summary>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4, marginTop: 8 }}>
                  {["1","2","3","4","5","6","7","8","9","*","0","#"].map(d => (
                    <button key={d} onClick={() => setManualNumber(p => p + d)}
                      style={{ padding: "10px 0", borderRadius: 6, border: "1px solid rgba(217,219,221,0.08)", background: "transparent", color: "#FAFAFA", fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                      {d}
                    </button>
                  ))}
                </div>
              </details>
            </>) : manualNumber && manualNumber.replace(/[^\d]/g, "").length >= 3 ? (
              /* ── Dial unmatched number ── */
              <div style={{ padding: 20 }}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 20, color: "#FAFAFA" }}>{manualNumber}</div>
                  <div style={{ fontSize: 11, color: T.tx3, marginTop: 2 }}>No matching contact found</div>
                </div>
                <div style={{ ...crd, padding: 16, marginBottom: 16 }}>
                  {calling ? (
                    <div>
                      <div style={{ textAlign: "center", marginBottom: 12 }}>
                        <div style={{ fontSize: 10, color: "#2E8B57", letterSpacing: ".08em", marginBottom: 4 }}>CONNECTED</div>
                        <div style={{ fontSize: 28, color: "#FAFAFA", fontVariantNumeric: "tabular-nums" }}>{fmt(time)}</div>
                      </div>
                      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                        <button onClick={handleToggleMute} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: muted ? "rgba(139,69,87,0.15)" : "transparent", color: muted ? "#E53935" : "#FAFAFA", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>{muted ? "Unmute" : "Mute"}</button>
                        <button onClick={handleHangUp} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(139,69,87,0.3)", background: "rgba(139,69,87,0.15)", color: "#E53935", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>End</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: dialing ? "#D4763A" : (dialerReady ? "#2E8B57" : T.tx3), marginBottom: 8 }}>{dialing ? "DIALING..." : (dialerReady ? "DIALER READY" : dialerStatus)}</div>
                      <button onClick={() => handleStartCall(manualNumber)} disabled={!dialerReady || dialing || calling}
                        style={{ padding: "12px 36px", borderRadius: 8, border: "none", background: (dialerReady && !dialing && !calling) ? "#4059AD" : "rgba(255,255,255,0.06)", color: (dialerReady && !dialing && !calling) ? "#D9DBDD" : T.tx3, fontSize: 13, cursor: (dialerReady && !dialing && !calling) ? "pointer" : "default", fontFamily: "inherit" }}>
                        Call {manualNumber}
                      </button>
                    </div>
                  )}
                  {/* Caller ID strip */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12, paddingTop: 10, borderTop: "1px solid rgba(217,219,221,0.06)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 9, color: T.tx3 }}>Calling as:</span>
                      <span style={{ fontSize: 10, color: "#FAFAFA" }}>{selectedCallerId || callerIdPreview?.number || "Default"}</span>
                    </div>
                  </div>
                </div>
                {/* Notes area for unmatched calls */}
                <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Call notes..."
                  style={{ width: "100%", minHeight: 100, padding: 12, borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.03)", color: "#FAFAFA", fontSize: 12, fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box" }} />
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <Icon name="phone" size={28} color={T.tx3} />
                <div style={{ fontSize: 14, color: "#FAFAFA", marginTop: 12 }}>Search a contact or enter a number</div>
                <div style={{ fontSize: 11, color: T.tx3, marginTop: 4 }}>Or start a Power Dialer session from your queue</div>
              </div>
            )}
          </div>

          {/* ── Drag Handle ── */}
          <div onMouseDown={startDrag}
            style={{ width: 5, cursor: "col-resize", background: "transparent", flexShrink: 0, position: "relative", zIndex: 10 }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(64,89,173,0.3)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <div style={{ position: "absolute", top: "50%", left: 0, width: 5, height: 40, borderRadius: 3, background: "rgba(217,219,221,0.15)", transform: "translateY(-50%)" }} />
          </div>

          {/* RIGHT: Tabbed Panel (Intel / Notes / Resources) */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
            {/* Tabs */}
            <div style={{ display: "flex", gap: 2, padding: "10px 20px", borderBottom: "1px solid rgba(217,219,221,0.08)", flexShrink: 0, background: "#2C2E33" }}>
              {["intel", "notes"].map(t => (
                <button key={t} onClick={() => setActiveTab(t)} style={tabBtn(activeTab === t)}>{t === "intel" ? "Intel" : "Notes & Resources"}</button>
              ))}
            </div>

            <div style={{ overflow: "auto", padding: 20, height: "calc(100vh - 200px)", maxHeight: "calc(92vh - 150px)" }}>
              {/* ── Intel Tab ── */}
              {activeTab === "intel" && (<>
                {preCallLoading && <div style={{ padding: 20, textAlign: "center" }}><div style={{ fontSize: 10, color: "#4059AD", letterSpacing: ".08em" }}>ANALYZING...</div></div>}
                {preCallIntel && (
                  <div>
                    {preCallIntel.sentiment && <div style={{ fontSize: 10, color: preCallIntel.sentiment === "hot" ? "#2E8B57" : preCallIntel.sentiment === "warm" ? "#D4763A" : T.tx3, marginBottom: 12 }}>{preCallIntel.sentiment.toUpperCase()}</div>}
                    {preCallIntel.opener && <div style={{ marginBottom: 16 }}><div style={lbl}>OPENER</div><div style={{ fontSize: 12, color: "#FAFAFA", fontStyle: "italic", lineHeight: 1.6, padding: "8px 12px", borderLeft: "2px solid #4059AD" }}>"{preCallIntel.opener}"</div></div>}
                    {preCallIntel.keyFacts?.length > 0 && <div style={{ marginBottom: 16 }}><div style={lbl}>KEY FACTS</div>{preCallIntel.keyFacts.map((f, i) => <div key={i} style={{ fontSize: 11, color: "#FAFAFA", lineHeight: 1.6, paddingLeft: 10, borderLeft: "2px solid rgba(217,219,221,0.12)", marginBottom: 4 }}>{f}</div>)}</div>}
                    {preCallIntel.talkingPoints?.length > 0 && <div style={{ marginBottom: 16 }}><div style={lbl}>TALKING POINTS</div>{preCallIntel.talkingPoints.map((t2, i) => <div key={i} style={{ fontSize: 11, color: "#FAFAFA", lineHeight: 1.6, marginBottom: 2 }}>· {t2}</div>)}</div>}
                    {preCallIntel.watchOut && <div style={{ marginBottom: 16 }}><div style={{ ...lbl, color: "#E53935" }}>WATCH OUT</div><div style={{ fontSize: 11, color: "#FAFAFA", lineHeight: 1.6 }}>{preCallIntel.watchOut}</div></div>}
                  </div>
                )}
                {!preCallIntel && !preCallLoading && activeContact?.ai && (
                  <div><div style={lbl}>CORE INTEL</div><div style={{ fontSize: 11, color: "#FAFAFA", lineHeight: 1.6 }}>{activeContact.ai}</div></div>
                )}
                {!activeContact && <div style={{ color: T.tx3, fontSize: 11, textAlign: "center", padding: 40 }}>Select a contact to see intel</div>}

                {/* Post-Call Summary */}
                {postCallSummary && (
                  <div style={{ ...crd, padding: 14, marginTop: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ ...lbl, marginBottom: 0 }}>POST-CALL SUMMARY</span>
                      <button onClick={() => setPostCallSummary(null)} style={{ background: "none", border: "none", color: T.tx3, cursor: "pointer", fontSize: 12 }}>×</button>
                    </div>
                    <div style={{ fontSize: 11, color: "#FAFAFA", lineHeight: 1.6, marginBottom: 8 }}>{postCallSummary.summary}</div>
                    {postCallSummary.followUpEmail && (
                      <div style={{ marginTop: 8 }}>
                        <div style={lbl}>FOLLOW-UP DRAFT</div>
                        <div style={{ fontSize: 10, color: "#4059AD", marginBottom: 4 }}>Subject: {postCallSummary.followUpSubject}</div>
                        <div style={{ fontSize: 10, color: "#FAFAFA", lineHeight: 1.5, padding: "8px 10px", background: "rgba(255,255,255,0.03)", borderRadius: 6, border: "1px solid rgba(217,219,221,0.08)" }}>{postCallSummary.followUpEmail}</div>
                        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                          <button onClick={() => { window.__coreflow_draft_email = { to: activeContact?.email, subject: postCallSummary.followUpSubject, body: postCallSummary.followUpEmail }; window.history.pushState({}, "", "/email"); }}
                            style={{ padding: "5px 12px", borderRadius: 6, border: "none", background: "#4059AD", color: "#D9DBDD", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>Send Email</button>
                          <button onClick={() => navigator.clipboard.writeText(postCallSummary.followUpEmail)}
                            style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>Copy</button>
                        </div>
                      </div>
                    )}
                    {postCallSummary.suggestedTask && (
                      <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ fontSize: 10, color: "#FAFAFA" }}>{postCallSummary.suggestedTask}</div>
                        <button onClick={() => { store.addTask({ title: postCallSummary.suggestedTask, contactId: activeContact?.id, due: new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10), pri: "medium" }); setPostCallSummary(null); }}
                          style={{ padding: "3px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#2E8B57", fontSize: 9, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>+ Task</button>
                      </div>
                    )}
                  </div>
                )}
              </>)}

              {/* ── Notes Tab ── */}
              {activeTab === "notes" && (
                <div>
                  <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Type notes during the call..."
                    style={{ width: "100%", minHeight: 200, padding: 14, borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.03)", color: "#FAFAFA", fontSize: 12, fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box", lineHeight: 1.6 }} />
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    <button onClick={() => {
                      if (!noteText.trim() || !activeContact) return;
                      logCall({ contactId: activeContact.id, title: "Note: " + noteText.slice(0, 60), duration: 0, disposition: "Note", notes: noteText });
                      store.addActivity && store.addActivity({ contactId: activeContact.id, type: "note", title: noteText.slice(0, 80), notes: noteText, date: new Date().toISOString() });
                      setNoteSaved(true); setTimeout(() => setNoteSaved(false), 2000);
                    }}
                      disabled={!noteText.trim() || !activeContact}
                      style={{ padding: "6px 16px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: noteText.trim() ? "#4059AD" : "transparent", color: noteText.trim() ? "#D9DBDD" : T.tx3, fontSize: 10, cursor: noteText.trim() ? "pointer" : "default", fontFamily: "inherit" }}>
                      {noteSaved ? "Saved" : "Save Note"}
                    </button>
                    {noteText.trim() && <button onClick={() => setNoteText("")} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: T.tx3, fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>Clear</button>}
                  </div>

                  {/* ── Quick SMS & Email ── */}
                  {activeContact && (
                    <div style={{ marginTop: 16, display: "flex", gap: 6 }}>
                      <button onClick={() => { setShowSmsCompose(!showSmsCompose); setShowEmailCompose(false); }}
                        style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: showSmsCompose ? "rgba(64,89,173,0.15)" : "transparent", color: "#FAFAFA", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>
                        SMS {activeContact.ph ? "" : "(no phone)"}
                      </button>
                      <button onClick={() => { setShowEmailCompose(!showEmailCompose); setShowSmsCompose(false); }}
                        style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: showEmailCompose ? "rgba(64,89,173,0.15)" : "transparent", color: "#FAFAFA", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>
                        Email {activeContact.email ? "" : "(no email)"}
                      </button>
                    </div>
                  )}

                  {/* SMS Compose */}
                  {showSmsCompose && activeContact && (
                    <div style={{ ...crd, padding: 14, marginTop: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <div style={{ fontSize: 10, color: T.tx3 }}>SMS to {activeContact.fn} · {activeContact.ph || "No phone"}</div>
                        <button onClick={() => setShowSmsCompose(false)} style={{ background: "none", border: "none", color: T.tx3, cursor: "pointer", fontSize: 12 }}>×</button>
                      </div>
                      <textarea value={smsText} onChange={e => setSmsText(e.target.value)} placeholder="Type your message..."
                        style={{ width: "100%", minHeight: 60, padding: 10, borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.03)", color: "#FAFAFA", fontSize: 11, fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box", lineHeight: 1.5 }} />
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                        <span style={{ fontSize: 9, color: T.tx3 }}>{smsText.length}/160</span>
                        <button onClick={sendQuickSms} disabled={!smsText.trim() || !activeContact?.ph || smsSending}
                          style={{ padding: "5px 14px", borderRadius: 6, border: "none", background: smsText.trim() && activeContact?.ph ? "#4059AD" : "rgba(255,255,255,0.06)", color: smsText.trim() && activeContact?.ph ? "#D9DBDD" : T.tx3, fontSize: 10, cursor: smsText.trim() ? "pointer" : "default", fontFamily: "inherit" }}>
                          {smsSent ? "Sent" : smsSending ? "Sending..." : "Send SMS"}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Email Compose */}
                  {showEmailCompose && activeContact && (
                    <div style={{ ...crd, padding: 14, marginTop: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <div style={{ fontSize: 10, color: T.tx3 }}>Email to {activeContact.fn} · {activeContact.email || "No email"}</div>
                        <button onClick={() => setShowEmailCompose(false)} style={{ background: "none", border: "none", color: T.tx3, cursor: "pointer", fontSize: 12 }}>×</button>
                      </div>
                      <input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} placeholder="Subject"
                        style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.03)", color: "#FAFAFA", fontSize: 11, fontFamily: "inherit", outline: "none", boxSizing: "border-box", marginBottom: 6 }} />
                      <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)} placeholder="Type your email..."
                        style={{ width: "100%", minHeight: 80, padding: 10, borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.03)", color: "#FAFAFA", fontSize: 11, fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box", lineHeight: 1.5 }} />
                      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
                        <button onClick={sendQuickEmail} disabled={!emailBody.trim() || !activeContact?.email || emailSending}
                          style={{ padding: "5px 14px", borderRadius: 6, border: "none", background: emailBody.trim() && activeContact?.email ? "#4059AD" : "rgba(255,255,255,0.06)", color: emailBody.trim() && activeContact?.email ? "#D9DBDD" : T.tx3, fontSize: 10, cursor: emailBody.trim() ? "pointer" : "default", fontFamily: "inherit" }}>
                          {emailSent ? "Sent" : emailSending ? "Sending..." : "Send Email"}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── Resources Section (read-only, configured in Settings → Teams) ── */}
                  <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid rgba(217,219,221,0.08)" }}>

                  {(resources.bookingLinks || []).filter(bl => bl.name && bl.url).length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                      <div style={lbl}>BOOKING LINKS</div>
                      {(resources.bookingLinks || []).filter(bl => bl.name && bl.url).map(bl => (
                        <div key={bl.id} style={{ ...crd, padding: 12, marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <div style={{ fontSize: 11, color: "#FAFAFA" }}>{bl.name}</div>
                            <div style={{ fontSize: 9, color: T.tx3 }}>{bl.url}</div>
                          </div>
                          <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                            <button onClick={() => window.open(bl.url, "_blank")} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>Open</button>
                            <button onClick={() => navigator.clipboard.writeText(bl.url)} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>Copy</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {(resources.scripts || []).filter(sc => sc.name || sc.content).length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                      <div style={lbl}>SALES SCRIPTS</div>
                      {(resources.scripts || []).filter(sc => sc.name || sc.content).map(sc => (
                        <div key={sc.id} style={{ ...crd, padding: 12, marginBottom: 6 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                            <div style={{ fontSize: 11, color: "#FAFAFA" }}>{sc.name || "Script"}</div>
                            <button onClick={() => navigator.clipboard.writeText(sc.content)} style={{ padding: "3px 8px", borderRadius: 4, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>Copy</button>
                          </div>
                          <div style={{ fontSize: 10, color: T.tx3, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{sc.content}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {(resources.docs || []).filter(d => d.name && d.url).length > 0 && (
                    <div>
                      <div style={lbl}>DOCUMENTS & COLLATERAL</div>
                      {(resources.docs || []).filter(d => d.name && d.url).map(doc => (
                        <div key={doc.id} style={{ ...crd, marginBottom: 6, overflow: "hidden" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 12 }}>
                            <div style={{ fontSize: 11, color: "#FAFAFA", cursor: "pointer" }} onClick={() => setExpandedDocs(prev => { const next = new Set(prev); if (next.has(doc.id)) next.delete(doc.id); else next.add(doc.id); return next; })}>
                              {expandedDocs.has(doc.id) ? "▾" : "▸"} {doc.name}
                            </div>
                            <div style={{ display: "flex", gap: 4 }}>
                              <button onClick={() => setExpandedDocs(prev => { const next = new Set(prev); if (next.has(doc.id)) next.delete(doc.id); else next.add(doc.id); return next; })}
                                style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>{expandedDocs.has(doc.id) ? "Collapse" : "Preview"}</button>
                              <button onClick={() => window.open(doc.url, "_blank")} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>New Tab</button>
                              <button onClick={() => navigator.clipboard.writeText(doc.url)} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>Copy</button>
                            </div>
                          </div>
                          {expandedDocs.has(doc.id) && (
                            <iframe src={doc.url.includes("docs.google.com") && !doc.url.includes("/preview") ? doc.url.replace(/\/edit.*$/, "/preview") : doc.url}
                              style={{ width: "100%", height: 700, border: "none", borderTop: "1px solid rgba(217,219,221,0.08)", background: "#1a1a1a" }}
                              title={doc.name} sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation" allow="clipboard-read; clipboard-write" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {(resources.bookingLinks || []).length === 0 && (resources.scripts || []).length === 0 && (resources.docs || []).length === 0 && (
                    <div style={{ fontSize: 10, color: T.tx3, textAlign: "center", padding: 20 }}>No resources configured. Ask your admin to set them up in Settings → Teams.</div>
                  )}

                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        </>)}
      </div>

      {/* ═══ SESSION CONFIG MODAL ═══ */}
      {showSessionConfig && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowSessionConfig(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, padding: isMobile ? 16 : 24, width: isMobile ? "92vw" : 380, boxShadow: "0 40px 100px rgba(0,0,0,0.4)" }}>
            <div style={{ fontSize: 14, color: "#FAFAFA", marginBottom: 4 }}>Power Dialer Session</div>
            <div style={{ fontSize: 11, color: T.tx3, marginBottom: 16 }}>{callQueue.length} contacts in queue</div>

            <div style={{ marginBottom: 16 }}>
              <div style={lbl}>Time between calls</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input type="range" min={1} max={120} value={autoDialDelay} onChange={e => setAutoDialDelay(Number(e.target.value))}
                  style={{ flex: 1 }} />
                <span style={{ fontSize: 13, color: "#FAFAFA", minWidth: 40, textAlign: "right" }}>{autoDialDelay}s</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: T.tx3, marginTop: 2 }}>
                <span>1 sec</span><span>2 min</span>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={startPowerDialer}
                style={{ flex: 1, padding: "12px 0", borderRadius: 8, border: "none", background: "#4059AD", color: "#D9DBDD", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                Start Session
              </button>
              <button onClick={() => setShowSessionConfig(false)}
                style={{ padding: "12px 20px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: T.tx3, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ INCOMING CALL BANNER ═══ */}
      {incomingCall && (
        <div style={{ position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)", background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 12, padding: "14px 24px", boxShadow: "0 20px 60px rgba(0,0,0,0.4)", zIndex: 9999, display: "flex", alignItems: "center", gap: 14 }}>
          <Icon name="phone" size={18} color="#2E8B57" />
          <div>
            <div style={{ fontSize: 12, color: "#FAFAFA" }}>Incoming Call</div>
            <div style={{ fontSize: 11, color: T.tx3 }}>{incomingCall.from}</div>
          </div>
          <button onClick={() => acceptIncoming()} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#2E8B57", color: "#fff", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Accept</button>
          <button onClick={() => { rejectIncoming(); setIncomingCall(null); }} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(139,69,87,0.3)", background: "rgba(139,69,87,0.15)", color: "#E53935", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Decline</button>
        </div>
      )}
    </div>
  );
}
